/**
 * utils/remoteStorage.js
 * Talks to the shared online copy of the books (Supabase) — multi-device sync, step 3.
 * No React, no UI, no localStorage. Used by RemoteBooksGate.js (opening the books) and
 * remoteSync.js (saving them) whenever the build has Supabase settings (step 4).
 *
 * The database side is `supabase/schema.sql`: ONE row holds the whole data object plus a
 * version number, and the public key can only call four functions — get_state, get_version,
 * save_state (with a version check), ping. This module is a thin client for exactly those four,
 * using plain `fetch` (no supabase-js: four RPC calls don't need it; it becomes worth adding
 * only when login arrives).
 *
 * Every call RESOLVES (never throws) to a plain result object, so callers branch on data, not
 * on exceptions:
 *   failure shape, for anything that isn't a real answer from the database:
 *     { ok:false, reason:"error", error:{ kind, status?, message } }
 *     kind: "offline"   — no connection (fetch itself failed)
 *           "timeout"   — no answer within the time limit
 *           "auth"      — 401/403: wrong/missing key (a setup problem, not a network one)
 *           "not_found" — 404: wrong URL, or schema.sql not run on this project
 *           "http"      — any other non-2xx answer
 *           "bad_response" — a 2xx answer that isn't the shape schema.sql returns
 */

const DEFAULT_TIMEOUTS = { load: 30000, save: 30000, version: 10000, ping: 10000 };

/**
 * Read the Supabase connection settings baked in at build time (Cloudflare sets them per site:
 * Production → client project, Preview → staging project; see supabase/README.md). Returns null
 * when either is missing or blank — that means "no shared copy here" (tests, `npm start`),
 * and the app keeps using localStorage.
 */
export function remoteConfigFromEnv(env = process.env) {
  const url = (env.REACT_APP_SUPABASE_URL || "").trim().replace(/\/+$/, "");
  const key = (env.REACT_APP_SUPABASE_KEY || "").trim();
  if (!url || !key) return null;
  return { url, key };
}

function errorResult(kind, message, status) {
  return { ok: false, reason: "error", error: status === undefined ? { kind, message } : { kind, status, message } };
}

function httpKind(status) {
  if (status === 401 || status === 403) return "auth";
  if (status === 404) return "not_found";
  return "http";
}

const toNum = (v) => (v === null || v === undefined ? null : Number(v));

/**
 * @param {{url: string, key: string}} config  from remoteConfigFromEnv
 * @param {{fetchImpl?: Function, timeouts?: object}} [options]  fetchImpl is injectable for tests
 */
export function createRemoteStore(config, { fetchImpl, timeouts = {} } = {}) {
  const doFetch = fetchImpl || ((...a) => fetch(...a));
  const limits = { ...DEFAULT_TIMEOUTS, ...timeouts };
  const base = `${config.url}/rest/v1/rpc`;

  // POST one RPC call. Resolves to { ok:true, body } or an errorResult — never throws.
  async function call(fn, params, timeoutMs) {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    let timedOut = false;
    const timer = controller ? setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs) : null;
    try {
      let res;
      try {
        res = await doFetch(`${base}/${fn}`, {
          method: "POST",
          headers: { apikey: config.key, "Content-Type": "application/json" },
          body: JSON.stringify(params),
          signal: controller ? controller.signal : undefined,
        });
      } catch (e) {
        if (timedOut) return errorResult("timeout", `Tidak ada jawaban dari server dalam ${Math.round(timeoutMs / 1000)} detik.`);
        return errorResult("offline", "Tidak dapat terhubung ke server.");
      }
      const text = await res.text();
      if (!res.ok) return errorResult(httpKind(res.status), text.slice(0, 300), res.status);
      try {
        return { ok: true, body: text ? JSON.parse(text) : null };
      } catch {
        return errorResult("bad_response", "Jawaban server tidak bisa dibaca.");
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  // get_state / get_version return a one-row array (see schema.sql).
  const singleRow = (r) => (Array.isArray(r.body) && r.body.length === 1 && r.body[0] && typeof r.body[0] === "object" ? r.body[0] : null);

  return {
    /** The whole books. `data` is null while the online copy has never been set up. */
    async getState() {
      const r = await call("get_state", {}, limits.load);
      if (!r.ok) return r;
      const row = singleRow(r);
      if (!row || !("data" in row) || !("version" in row)) return errorResult("bad_response", "Data buku tidak ditemukan di server.");
      if (row.data !== null && (typeof row.data !== "object" || Array.isArray(row.data))) {
        return errorResult("bad_response", "Data buku di server bentuknya tidak dikenal.");
      }
      return {
        ok: true,
        data: row.data,
        version: toNum(row.version),
        normVersion: toNum(row.norm_version),
        updatedAt: row.updated_at || null,
        updatedBy: row.updated_by || null,
      };
    },

    /** Just the version numbers and who/when — the cheap "has anything changed?" check. */
    async getVersion() {
      const r = await call("get_version", {}, limits.version);
      if (!r.ok) return r;
      const row = singleRow(r);
      if (!row || !("version" in row)) return errorResult("bad_response", "Versi buku tidak ditemukan di server.");
      return {
        ok: true,
        version: toNum(row.version),
        normVersion: toNum(row.norm_version),
        updatedAt: row.updated_at || null,
        updatedBy: row.updated_by || null,
      };
    },

    /**
     * Save the whole books, based on `expectedVersion`. Resolves to one of:
     *   { ok:true, version }                                         saved
     *   { ok:false, reason:"conflict", version, normVersion, updatedBy }  someone saved first
     *   { ok:false, reason:"outdated_app", version, normVersion }    data is in a newer format
     *   { ok:false, reason:"too_large", bytes, limit }                over the 20 MB limit
     *   { ok:false, reason:"invalid" }                               refused as not real data
     *   { ok:false, reason:"error", error }                          no real answer (see top)
     */
    async saveState({ expectedVersion, data, normVersion, savedBy = null }) {
      const r = await call("save_state", {
        p_expected_version: expectedVersion,
        p_data: data,
        p_norm_version: normVersion,
        p_saved_by: savedBy,
      }, limits.save);
      if (!r.ok) return r;
      const b = r.body;
      if (!b || typeof b !== "object" || typeof b.ok !== "boolean") return errorResult("bad_response", "Jawaban simpan tidak dikenal.");
      if (b.ok) return { ok: true, version: toNum(b.version) };
      switch (b.reason) {
        case "conflict":
        case "outdated_app":
          return { ok: false, reason: b.reason, version: toNum(b.version), normVersion: toNum(b.norm_version), updatedBy: b.updated_by || null };
        case "too_large":
          return { ok: false, reason: "too_large", bytes: toNum(b.bytes), limit: toNum(b.limit) };
        case "invalid":
          return { ok: false, reason: "invalid" };
        default:
          return errorResult("bad_response", `Alasan penolakan tidak dikenal: ${String(b.reason)}`);
      }
    },

    /** The keep-awake call cron-job.org uses; handy as a plain "can we reach it?" check. */
    async ping() {
      const r = await call("ping", {}, limits.ping);
      if (!r.ok) return r;
      return typeof r.body === "string" && r.body.startsWith("ok") ? { ok: true } : errorResult("bad_response", "Jawaban ping tidak dikenal.");
    },
  };
}
